import { LocalizedString } from "../i18n/i18n-types";
import { DatabaseCode, toDatabaseCode } from "./database-code";

export type DispatchNotificationSkipReason = DatabaseCode & {
  description: LocalizedString
}

const cooldown: DispatchNotificationSkipReason = {
  code: "COOLDOWN",
  displayValue: {
    id: "common.entity.dispatchnotification.skipreason.cooldown",
    defaultMessage: "Cooldown",
  },
  description: {
    id: "common.entity.dispatchnotification.skipreason.cooldown.description",
    defaultMessage: "Skipped to prevent repeated notifications for the same dispatch version within a short time window.",
  },
};

const quietHours: DispatchNotificationSkipReason = {
  code: "QUIET_HOURS",
  displayValue: {
    id: "common.entity.dispatchnotification.skipreason.quiet_hours",
    defaultMessage: "Quiet hours",
  },
  description: {
    id: "common.entity.dispatchnotification.skipreason.quiet_hours.description",
    defaultMessage: "Skipped because delivery occurred during configured quiet hours.",
  },
};

const offShift: DispatchNotificationSkipReason = {
  code: "OFF_SHIFT",
  displayValue: {
    id: "common.entity.dispatchnotification.skipreason.off_shift",
    defaultMessage: "Off shift",
  },
  description: {
    id: "common.entity.dispatchnotification.skipreason.off_shift.description",
    defaultMessage: "Skipped because the driver is outside their assigned shift window.",
  },
};

const noAssignedDriver: DispatchNotificationSkipReason = {
  code: "NO_ASSIGNED_DRIVER",
  displayValue: {
    id: "common.entity.dispatchnotification.skipreason.no_assigned_driver",
    defaultMessage: "No assigned driver",
  },
  description: {
    id: "common.entity.dispatchnotification.skipreason.no_assigned_driver.description",
    defaultMessage: "Skipped because the dispatch does not have an assigned driver.",
  },
};

const dispatchNotActive: DispatchNotificationSkipReason = {
  code: "DISPATCH_NOT_ACTIVE",
  displayValue: {
    id: "common.entity.dispatchnotification.skipreason.dispatch_not_active",
    defaultMessage: "Dispatch not active",
  },
  description: {
    id: "common.entity.dispatchnotification.skipreason.dispatch_not_active.description",
    defaultMessage: "Skipped because the dispatch is not in an active state eligible for notification.",
  },
};

const rateLimit: DispatchNotificationSkipReason = {
  code: "RATE_LIMIT",
  displayValue: {
    id: "common.entity.dispatchnotification.skipreason.rate_limit",
    defaultMessage: "Rate limit",
  },
  description: {
    id: "common.entity.dispatchnotification.skipreason.rate_limit.description",
    defaultMessage: "Skipped because the sender or tenant exceeded the notification rate limit.",
  },
};

const notEligible: DispatchNotificationSkipReason = {
  code: "NOT_ELIGIBLE",
  displayValue: {
    id: "common.entity.dispatchnotification.skipreason.not_eligible",
    defaultMessage: "Not eligible",
  },
  description: {
    id: "common.entity.dispatchnotification.skipreason.not_eligible.description",
    defaultMessage: "Skipped because the recipient is not subscribed, was filtered out, or context could not be generated.",
  },
};

export const dispatchNotificationSkipReason = {
  cooldown,
  quietHours,
  offShift,
  noAssignedDriver,
  dispatchNotActive,
  rateLimit,
  notEligible,
};

export const dispatchNotificationSkipReasons = Object.values(dispatchNotificationSkipReason);

export const toDispatchNotificationSkipReason = (code: string): DispatchNotificationSkipReason =>
  toDatabaseCode(code, dispatchNotificationSkipReasons);

export const toDispatchNotificationSkipReasonSafely = (
  code: string | undefined
): DispatchNotificationSkipReason | undefined =>
  code ? toDatabaseCode(code, dispatchNotificationSkipReasons) : undefined;
